import {CinemaTransport,CHAPTERS,TRANSITION_MS} from './transport';
import type {MarketingFrame,SectionId} from '../types';
import {sampleWorkflow,WORKFLOW_SCORE} from '../editorial/workflowScore';
import {mountCinemaSound,type CinemaSound} from '../sound';
import {cinemaCopy} from './copy';
const smooth=(v:number)=>{const t=Math.max(0,Math.min(1,v));return t*t*(3-2*t);};
const mix=(a:number,b:number,t:number)=>a+(b-a)*t;

/** Sole input/clock owner. The existing world and proof renderers remain the actors. */
export class CinemaDirector {
 readonly transport=new CinemaTransport();
 private last:number|null=null;
 private origin=0;
 private lastVisit=-1;
 private gesture=0;
 private touchY=0;
 private touchX=0;
 private abort=new AbortController();
 private cue:HTMLButtonElement;
 private pauseButton:HTMLButtonElement;
 private startButton:HTMLButtonElement;
 private frameTime=0;
 private worldEpoch=0;
 private navigationRequested=false;
 private reverseGestureAt=-Infinity;
 private reverseGestureDistance=0;
 private reverseGestureHandled=false;
 reviewing=false;
 private scrollRestoration:ScrollRestoration;
 private sound:CinemaSound;
 private actorsComplete(){
  const id=this.chapter.id;
  if(this.view.matchMedia('(prefers-reduced-motion: reduce)').matches)return true;
  if(id==='uses-site')return this.root.querySelector('#uses-site')?.getAttribute('data-highlights')==='complete';
  if(id==='uses-game')return this.root.querySelector('#uses-game')?.getAttribute('data-game-complete')==='true';
  if(id==='uses-more')return this.root.querySelector('[data-creative-showcase="capabilities"]')?.getAttribute('data-creative-complete')==='true';
  return true;
 }
 constructor(private view:Window,private root:HTMLElement){
  const doc=root.ownerDocument;
  this.scrollRestoration=view.history.scrollRestoration;view.history.scrollRestoration='manual';
  view.scrollTo({top:0,behavior:'instant'});
  this.startButton=doc.createElement('button');this.startButton.className='cinema-start';this.startButton.type='button';
  this.startButton.innerHTML='<span>Смотреть, как это работает</span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 4v16m-6-6 6 6 6-6"/></svg>';
  doc.body.append(this.startButton);
  this.cue=doc.createElement('button');this.cue.className='cinema-cue';this.cue.type='button';this.cue.hidden=true;
  this.cue.innerHTML='<span></span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 4v16m-6-6 6 6 6-6"/></svg>';
  this.pauseButton=doc.createElement('button');this.pauseButton.type='button';this.pauseButton.className='bb-btn cinema-pause';this.pauseButton.textContent='Пауза';this.pauseButton.hidden=true;
  doc.body.append(this.cue,this.pauseButton);
  this.sound=mountCinemaSound(view);
  const signal=this.abort.signal;
  doc.addEventListener('visibilitychange',()=>{this.last=null;},{signal});
  const updateCopy=()=>{
   const copy=cinemaCopy(doc.documentElement.lang);
   this.startButton.querySelector('span')!.textContent=copy.start;
   doc.querySelector('.nav-sections')?.setAttribute('aria-label',copy.navigation);
   doc.querySelectorAll('[data-cinema-link]').forEach((node,index)=>{if(copy.links[index])node.textContent=copy.links[index];});
  };
  updateCopy();view.addEventListener('bb:localechange',updateCopy,{signal});
  doc.querySelector('#nav .logo')?.addEventListener('click',e=>{e.preventDefault();this.returnToHero();},{signal});
  for(const link of doc.querySelectorAll<HTMLAnchorElement>('[data-cinema-link]'))link.addEventListener('click',e=>{e.preventDefault();this.goTo(link.dataset.cinemaLink!);},{signal});
  this.startButton.addEventListener('click',()=>this.start(),{signal});
  this.cue.addEventListener('click',()=>this.request(1),{signal});
  this.pauseButton.addEventListener('click',()=>this.reviewing||['ready','ended'].includes(this.state.phase)?this.replay():this.togglePause(),{signal});
  const interactive=(target:EventTarget|null)=>target instanceof Element&&Boolean(target.closest('input,textarea,select,video,[data-video-player],.bb-video-player,.cinema-pause,.lang,[role="listbox"],[data-native-scroll]'));
  view.addEventListener('wheel',e=>{
   if(interactive(e.target)||e.ctrlKey||Math.abs(e.deltaX)>Math.abs(e.deltaY))return;
   e.preventDefault();e.stopImmediatePropagation();
   if(this.state.phase==='hero'){if(e.deltaY>28)this.start();return;}
   if(e.deltaY<0){this.reverseWheel(e.deltaY,e.timeStamp);return;}
   if(this.state.phase!=='ready'&&this.state.phase!=='ended'){this.gesture=0;return;}
   this.gesture+=e.deltaY;if(Math.abs(this.gesture)>28){this.request(this.gesture>0?1:-1);this.gesture=0;}
  },{capture:true,passive:false,signal});
  view.addEventListener('touchstart',e=>{this.touchY=e.touches[0]?.clientY??0;this.touchX=e.touches[0]?.clientX??0;},{passive:true,signal});
  view.addEventListener('touchmove',e=>{
   if(interactive(e.target))return;
   if(e.target instanceof Element&&e.target.closest('.offer-pricing__grid')&&Math.abs((e.touches[0]?.clientX??0)-this.touchX)>Math.abs((e.touches[0]?.clientY??0)-this.touchY))return;
   e.preventDefault();e.stopImmediatePropagation();
   const delta=this.touchY-(e.touches[0]?.clientY??this.touchY);if(Math.abs(delta)>26){if(this.state.phase==='hero'){if(delta>0)this.start();}else this.request(delta>0?1:-1);this.touchY=e.touches[0]?.clientY??this.touchY;}
  },{capture:true,passive:false,signal});
  view.addEventListener('keydown',e=>{
   if(interactive(e.target))return;
   if(e.key==='Escape'&&doc.querySelector('.lang.open'))return;
   if(this.state.phase==='hero'){if(['ArrowDown','PageDown',' '].includes(e.key)&&!(e.target instanceof Element&&e.target.closest('button,a'))){e.preventDefault();e.stopImmediatePropagation();this.start();}return;}
   if(e.key==='Escape'){e.preventDefault();e.stopImmediatePropagation();this.togglePause();return;}
   if(['Home','End'].includes(e.key)){e.preventDefault();e.stopImmediatePropagation();if(e.key==='Home')this.returnToHero();else this.goTo('footer');return;}
   if(['ArrowDown','PageDown','ArrowUp','PageUp',' '].includes(e.key)&&!(e.target instanceof Element&&e.target.closest('button,a'))){e.preventDefault();e.stopImmediatePropagation();this.request(['ArrowUp','PageUp'].includes(e.key)?-1:1);}
  },{capture:true,signal});
  /* Приход по ссылке — это переход, а не премьера. Раньше глава ставилась на
     нулевую секунду и проигрывалась заново: возврат кнопкой «назад» из
     «Политики конфиденциальности» заново показывал уход в подвал, который
     человек только что видел. `goTo` сам решает, показывать главу или отдать
     её конечный кадр. */
  const linked=view.location.hash.slice(1);
  if(CHAPTERS.some(chapter=>chapter.id===linked)&&linked!=='film')this.goTo(linked);
 }
 get state(){return this.transport.state;}
 get active(){return this.state.phase!=='hero';}
 get transitioning(){return this.state.phase==='transition';}
 get transitionProgress(){return Math.min(1,this.state.transition/(this.chapter.transitionMs??TRANSITION_MS));}
 get departureHold(){return this.chapter.departureHold??.12;}
 get now(){return this.active?this.worldEpoch+this.state.clock:this.frameTime;}
 get chapter(){return CHAPTERS[this.state.chapter]!;}
 get headerVisible(){return !this.active||this.navigationRequested||this.state.paused||this.state.phase==='ended'||this.chapter.id==='footer'&&this.state.elapsed>=this.chapter.duration-450;}
 start(){this.worldEpoch=this.frameTime-this.state.clock;this.reviewing=false;this.startButton.hidden=true;this.transport.start();this.navigationRequested=false;this.origin=this.view.scrollY;this.last=null;this.root.dataset.cinema='true';if(this.view.matchMedia('(prefers-reduced-motion: reduce)').matches){this.state.chapter=1;this.state.elapsed=CHAPTERS[1]!.duration;this.state.phase='ready';}}
 goTo(id:string){
  if(!this.active)this.worldEpoch=this.frameTime-this.state.clock;
  if(id==='film'){this.returnToHero();this.start();return;}
  const chapter=CHAPTERS.findIndex(c=>c.id===id);if(chapter<1)return;
  const seen=this.transport.completed.has(chapter);
  this.reviewing=seen;this.startButton.hidden=true;
  const direct=seen||['pricing','install','footer'].includes(id);
  Object.assign(this.state,{chapter,elapsed:direct?CHAPTERS[chapter]!.duration:0,phase:direct?(id==='footer'?'ended':'ready'):'playing',transition:0,paused:false,visit:this.state.visit+1});
  this.root.dataset.cinema='true';this.navigationRequested=true;this.last=null;this.origin=this.anchor(chapter);
  this.view.scrollTo({top:this.origin,behavior:'instant'});
  this.view.history.replaceState(this.view.history.state,'',`#${id}`);
 }
 returnToHero(){
  this.reviewing=false;this.startButton.hidden=false;
  this.root.ownerDocument.querySelectorAll('[data-cinema-link][aria-current]').forEach(link=>link.removeAttribute('aria-current'));
  Object.assign(this.state,{phase:'hero',chapter:0,elapsed:0,transition:0,paused:false,visit:this.state.visit+1});
  this.last=null;this.navigationRequested=false;this.cue.hidden=true;this.pauseButton.hidden=true;delete this.root.dataset.cinema;
  for(const node of this.root.querySelectorAll<HTMLElement>('[data-section-id]')){node.style.removeProperty('visibility');delete node.dataset.cinemaHidden;node.inert=false;}
  this.view.history.replaceState(this.view.history.state,'',this.view.location.pathname+this.view.location.search);
  this.view.scrollTo({top:0,behavior:'instant'});
 }
 private reverseWheel(delta:number,at:number){
  if(at-this.reverseGestureAt>700){this.reverseGestureDistance=0;this.reverseGestureHandled=false;}
  this.reverseGestureAt=at;this.gesture=0;this.reverseGestureDistance-=delta;
  if(this.reverseGestureHandled||this.reverseGestureDistance<=12)return;
  this.reverseGestureHandled=true;
  // One burst has one intent, including its low-amplitude inertial tail.
  if(this.headerVisible)this.request(-1);else this.navigationRequested=true;
 }
 request(direction:1|-1){
  if(direction===-1||(!this.state.paused&&['ready','ended'].includes(this.state.phase)&&this.transport.completed.has(this.state.chapter+1))){
   if(direction===-1&&this.state.chapter<=1){this.returnToHero();return;}
   const chapter=this.state.chapter+direction;
   this.reviewing=true;this.navigationRequested=true;this.transport.completed.add(chapter);
   Object.assign(this.state,{chapter,elapsed:CHAPTERS[chapter]!.duration,phase:'ready',transition:0,paused:false,visit:this.state.visit+1});
   this.origin=this.anchor(chapter);this.last=null;this.gesture=0;
   this.view.scrollTo({top:this.origin,behavior:'instant'});
   this.view.history.replaceState(this.view.history.state,'',`#${this.chapter.id}`);
   return;
  }
  if(this.state.paused||!['ready','ended'].includes(this.state.phase))return;
  this.reviewing=false;
  this.navigationRequested=false;
  this.transport.request(direction);
  if(this.state.phase==='transition')this.origin=this.view.scrollY;
 }
 private replay(){const id=this.chapter.id;this.goTo(id);this.reviewing=false;this.state.elapsed=0;this.state.phase='playing';this.navigationRequested=false;}
 get workflow(){return {...sampleWorkflow(this.state.elapsed),score:WORKFLOW_SCORE};}
 private togglePause(){if(this.state.paused){this.transport.resume();this.navigationRequested=false;}else{this.transport.pause();this.navigationRequested=true;}}
 private base(){return (this.root.ownerDocument.getElementById('film')?.offsetTop??0)+this.view.BB_FILM_SCORE.endVh*this.view.innerHeight;}
 private anchor(index:number){
  const id=CHAPTERS[index]!.id;if(id==='film')return 0;
  const workflow=this.root.querySelector<HTMLElement>('#workflow')!;
  if(id==='intro')return this.base()+workflow.offsetHeight*.14;
  if(id==='workflow')return this.base()+workflow.offsetHeight*.2;
  const node=this.root.querySelector<HTMLElement>(`#${id}`)!;
  return this.base()+node.offsetTop+Math.max(0,node.offsetHeight-this.view.innerHeight)*.3;
 }
 tick(now:number){
  this.frameTime=now;const delta=this.last===null?0:now-this.last;this.last=now;
  this.startButton.hidden=this.active||!this.root.ownerDocument.body.classList.contains('intro-complete');
  if(!this.active)return;
  const departureComplete=this.chapter.id!=='uses-game'||this.reviewing||this.view.matchMedia('(prefers-reduced-motion: reduce)').matches||this.root.querySelector('#uses-game')?.getAttribute('data-game-exit-complete')==='true';
  const wasTransitioning=this.transitioning;
  this.transport.tick(delta,this.root.ownerDocument.hidden,this.actorsComplete(),departureComplete);
  if(!wasTransitioning&&this.transitioning)this.origin=this.view.scrollY;
  const s=this.state,id=this.chapter.id,t=s.elapsed/this.chapter.duration;
  let y=this.anchor(s.chapter);
  if(id==='film'){
   const opening=(this.root.ownerDocument.getElementById('film')?.offsetTop??0)+this.view.innerHeight*2;
   // Original opening rail; the camera owns the depth easing.
   const openingMs=2400;
   y=s.elapsed<openingMs?mix(this.origin,opening,s.elapsed/openingMs):mix(opening,this.base(),(s.elapsed-openingMs)/(this.chapter.duration-openingMs));
  }
  if(id==='intro')y=mix(this.base(),this.anchor(s.chapter),smooth(t));
  if(id==='workflow'){
   const workflow=this.root.querySelector<HTMLElement>('#workflow')!;
   const {index:beat,local}=sampleWorkflow(s.elapsed);
   const from=beat===0?.2:.12+beat*.2,to=.32+beat*.2;
   y=this.base()+workflow.offsetHeight*mix(from,to,smooth(local/1800));
  }
  if(this.transitioning)y=mix(this.origin,this.anchor(s.destination),smooth((this.transitionProgress-this.departureHold)/(1-this.departureHold)));
  this.view.scrollTo({top:y,behavior:'instant'});
  this.root.dataset.cinemaPhase=s.phase;this.root.dataset.cinemaChapter=id;
  if(this.view.matchMedia('(prefers-reduced-motion: reduce)').matches&&s.phase==='playing'&&s.elapsed>=600)s.elapsed=this.chapter.duration;
  this.root.dataset.cinemaElapsed=String(Math.round(s.elapsed));
  this.cue.hidden=s.phase!=='ready'||s.paused;
  const copy=cinemaCopy(this.root.ownerDocument.documentElement.lang);
  const cueText=id==='pricing'?copy.download:copy.next,cueLabel=this.cue.querySelector('span')!;
  if(cueLabel.textContent!==cueText)cueLabel.textContent=cueText;
  cueLabel.hidden=!['intro','pricing'].includes(id);this.cue.dataset.arrowOnly=String(cueLabel.hidden);this.cue.setAttribute('aria-label',cueText);
  this.pauseButton.hidden=false;
  const pauseText=this.reviewing||['ready','ended'].includes(s.phase)?copy.replay:s.paused?copy.resume:copy.pause;if(this.pauseButton.textContent!==pauseText)this.pauseButton.textContent=pauseText;
  this.root.dataset.cinemaPaused=String(s.paused);
  for(const link of this.root.ownerDocument.querySelectorAll<HTMLElement>('[data-cinema-link]')){
   const selected=link.dataset.cinemaLink===id;if(selected&&link.getAttribute('aria-current')!=='location')link.setAttribute('aria-current','location');else if(!selected&&link.hasAttribute('aria-current'))link.removeAttribute('aria-current');
  }
  if(this.lastVisit!==s.visit){
   this.lastVisit=s.visit;for(const node of this.root.querySelectorAll<HTMLElement>('[data-section-id]'))node.dataset.cinemaVisit=String(s.visit);
   this.gesture=0;
  }
  for(const node of this.root.querySelectorAll<HTMLElement>('[data-section-id]')){
   const permitted=id==='intro'||id==='workflow'?['workflow','where'].includes(node.id):node.id===id;
   const visibility=permitted?'':'hidden';if(node.style.visibility!==visibility)node.style.visibility=visibility;
   const hidden=String(!permitted);if(node.dataset.cinemaHidden!==hidden)node.dataset.cinemaHidden=hidden;
   if(node.inert===permitted)node.inert=!permitted;
  }
 }
 frame(frame:MarketingFrame):MarketingFrame{
  if(this.reviewing)frame={...frame,motion:'reduced'};
  if(!this.active||this.chapter.id==='film'||this.chapter.id==='intro'||this.chapter.id==='workflow')return frame;
  const id=this.chapter.id as SectionId;
  const sections={...frame.sections};
  sections[id]={progress:id==='footer'?(this.transitioning?1-smooth(this.transitionProgress):Math.min(1,this.state.elapsed/this.chapter.duration)):smooth(this.state.elapsed/this.chapter.duration)};
  return {...frame,activeSection:id,sections};
 }
 destroy(){this.abort.abort();this.sound.destroy();this.cue.remove();this.pauseButton.remove();this.startButton.remove();this.view.history.scrollRestoration=this.scrollRestoration;delete this.root.dataset.cinema;for(const n of this.root.querySelectorAll<HTMLElement>('[data-section-id]')){n.style.removeProperty('visibility');delete n.dataset.cinemaHidden;n.inert=false;}if(this.view.__BB_CINEMA__===this)delete this.view.__BB_CINEMA__;}
}
declare global {interface Window {__BB_CINEMA__?:CinemaDirector;BB_FILM_SCORE:{endVh:number;sample(travelVh:number):number}}}
export function mountCinema(view:Window,root:HTMLElement){return view.__BB_CINEMA__??(view.__BB_CINEMA__=new CinemaDirector(view,root));}
